import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { formatScopeCriteria } from '~/components/GHADiscounting/utils';

import { useGHAEffectiveDiscount } from '~/hooks/apis/connection-google/useGHAEffectiveDiscounts';

import { GHAAccountType, GHAEffectiveDiscountLeg } from '~/services/ConnectionGoogleService';

import { buildEffectiveDiscountMatrix, formatEffectiveDiscount } from './utils';

// The GHA Discounting page (Marketing tab) — scope rules and exclusions are managed there, so
// winning/blocking scoped rules link through to it with the rule focused.
const DISCOUNTING_PATH = '/marketing/gha-property-discount-summary';

const NONE_REASON_LABELS: Record<string, string> = {
  world_leg: 'The ZZ world leg is never discounted',
  no_scope_rule_matched: 'No scope rule matches this leg',
};

function legTooltip(leg: GHAEffectiveDiscountLeg): string {
  switch (leg.source) {
    case 'property':
      return 'Property rule (see the Active tab)';
    case 'scope_rule':
      return leg.scope
        ? `Scope rule (priority ${leg.scope.priority}): ${formatScopeCriteria(leg.scope)}`
        : 'Scope rule';
    case 'exclusion':
      return leg.scope ? `Exclusion rule: ${formatScopeCriteria(leg.scope)}` : 'Exclusion rule';
    case 'blacklist':
      return 'Property is blacklisted from discounting';
    default:
      return NONE_REASON_LABELS[leg.reason ?? ''] ?? 'No discount';
  }
}

function LegCell({ leg }: { leg?: GHAEffectiveDiscountLeg }) {
  if (!leg) {
    return <TableCell align="center">—</TableCell>;
  }

  let content: React.ReactNode;
  if (leg.outcome === 'discounted') {
    const label = formatEffectiveDiscount(leg.discountType, leg.discountValue);
    content =
      leg.source === 'scope_rule' && leg.ruleId ? (
        <Link
          href={`${DISCOUNTING_PATH}?section=scope-rules&rule=${leg.ruleId}`}
          target="_blank"
          rel="noopener noreferrer"
          color="success.main"
        >
          {label}
        </Link>
      ) : (
        <Typography component="span" variant="body2" color="success.main">
          {label}
        </Typography>
      );
  } else if (leg.outcome === 'blocked') {
    content =
      leg.source === 'exclusion' && leg.ruleId ? (
        <Link
          href={`${DISCOUNTING_PATH}?section=exclusions&rule=${leg.ruleId}`}
          target="_blank"
          rel="noopener noreferrer"
          color="error.main"
        >
          Blocked
        </Link>
      ) : (
        <Typography component="span" variant="body2" color="error.main">
          Blocked
        </Typography>
      );
  } else {
    content = (
      <Typography component="span" variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <TableCell align="center">
      <Tooltip title={legTooltip(leg)} arrow>
        <Box component="span">{content}</Box>
      </Tooltip>
    </TableCell>
  );
}

interface Props {
  propertyId: string;
  accountTypeOptions: Array<{ value: GHAAccountType; label: string }>;
}

/** What this property would emit to Google per (supplier x viewer country) leg, resolved by the
 *  backend through the same path as emission — so this cannot drift from what Google is served. */
function GHAEffectiveDiscountTab({ propertyId, accountTypeOptions }: Props) {
  const [accountType, setAccountType] = useState<GHAAccountType | undefined>(accountTypeOptions[0]?.value);

  // Keep the selection valid when the dialog's account context changes.
  useEffect(() => {
    if (!accountTypeOptions.some((option) => option.value === accountType)) {
      setAccountType(accountTypeOptions[0]?.value);
    }
  }, [accountType, accountTypeOptions]);

  const { data, isPending, isFetching, isError, error } = useGHAEffectiveDiscount(propertyId, accountType);

  if (!accountType) {
    return null;
  }

  const matrix = isError || isPending ? null : buildEffectiveDiscountMatrix(data.property.legs);

  // The header (and its account selector) renders in every state — an error on one account must
  // not take away the control that switches back to a working one.
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {matrix != null && (
            <>
              Assumes check-in {data.itinerary.checkIn} · {data.itinerary.nights} night
              {data.itinerary.nights === 1 ? '' : 's'}
              {data.property.abw != null ? ` · ${data.property.abw} days ahead` : ''} · one column per viewer country
            </>
          )}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Link href={DISCOUNTING_PATH} target="_blank" rel="noopener noreferrer" variant="body2">
            Open GHA Discounting
          </Link>
          {accountTypeOptions.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="effective-discount-account-label">Account Type</InputLabel>
              <Select
                labelId="effective-discount-account-label"
                label="Account Type"
                value={accountType}
                onChange={(event) => setAccountType(event.target.value)}
              >
                {accountTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Stack>

      {isPending && (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {isError && (
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to resolve the effective discounts'}
        </Alert>
      )}

      {matrix != null && (
        <>
          {!data.property.liveForAccount && (
            <Alert severity="warning">
              This account does not currently serve the property on Google — the legs below are a preview only.
            </Alert>
          )}
          {!data.scopedRulesLive && (
            <Alert severity="info">Scope rules resolve here as a preview — they are not emitting to Google yet.</Alert>
          )}

          {/* keepPreviousData holds the old matrix while an account switch refetches; dim it so the
              stale rows aren't mistaken for the new account's. */}
          <TableContainer sx={{ overflowX: 'auto', opacity: isFetching ? 0.5 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  {matrix.countries.map((country) => (
                    <TableCell key={country} align="center">
                      {country}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrix.rows.map((row) => (
                  <TableRow key={row.supplier ?? 'none'}>
                    <TableCell>{row.supplierName ?? row.supplier ?? 'No supplier'}</TableCell>
                    {matrix.countries.map((country) => (
                      <LegCell key={country} leg={row.legByCountry[country]} />
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
}

export default GHAEffectiveDiscountTab;
